import type { DelayedAction } from '../../api/ReplicantAPI';
import type DB from '../../db/DB';
import type { WebhookEvent } from '../../ReplicantChatbot';
import { resolveEventHandlerAndWriteMessage } from '../../ReplicantEventHandlerMessages';
import type { APIAccessInterface } from '../ServerReplicant';
import { withIndexDocGeneration } from '../utils/IndexUtils';

export async function executeOnWebhook(
    userId: string,
    events: WebhookEvent[],
    serverReplicant: APIAccessInterface<any>,
    db: DB<any>,
    opts?: {
        /** @default true */
        createUserIfNotFound?: boolean;
    },
): Promise<{
    newIndexDoc?: Record<string, unknown>;
}> {
    const onWebhook = serverReplicant.config.chatbot?.events?.onWebhook;

    if (!onWebhook) {
        return {};
    }

    const getEntryResult = await serverReplicant.getReadOnlyEntry(userId, {
        assignABTests: serverReplicant.config.assignAbTestsOnWebhook,
        createIfNotFound: opts?.createUserIfNotFound ?? true,
        reduceMessages: true,
    });

    if (!getEntryResult) {
        const error = Error(`Cannot execute onWebhook: Replicant user not found`) as Error & { userId: string };
        error.userId = userId;

        throw error;
    }

    const { entry, isNewUser } = getEntryResult;

    const delayedActions: DelayedAction[] = [];

    const eventHandlerApi = await serverReplicant.createEventHandlerApi({
        consistentFetchIds: [],
        delayedActions,
        entry,
        userId,
    });

    const handleOnWebhookEvents = async (): Promise<void> => {
        for (const event of events) {
            await onWebhook(entry.state, event, eventHandlerApi);
        }

        // validate the state after all events have been processed
        serverReplicant.config.stateSchema.tryValidate(entry.state);

        await serverReplicant.resolveDelayedActions(delayedActions);
    };

    const newIndexDoc = await withIndexDocGeneration(
        userId,
        entry,
        serverReplicant.config,
        serverReplicant.dateNow,
        isNewUser,
        () =>
            resolveEventHandlerAndWriteMessage({
                db,
                entry,
                event: { type: 'onWebhook' },
                eventHandler: handleOnWebhookEvents,
                now: serverReplicant.dateNow(),
                userId,
            }),
    );

    return { newIndexDoc: newIndexDoc ?? undefined };
}
